import { FormEvent, useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { usePortfolio } from "../hooks/usePortfolio";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { useToast } from "../components/ui/use-toast";
import { AllocationDonut } from "../components/charts/AllocationDonut";
import { SelectRemote } from "../components/ui/select-remote";
import { useTranslation } from "react-i18next";

const PortfolioPage = () => {
  const { user } = useAuth();
  const {
    summary,
    balances,
    loading,
    balancesLoading,
    error,
    fetchSummary,
    fetchBalances,
    upsertPosition,
  } = usePortfolio();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    assetSymbol: "",
    assetName: "",
    investedAmount: "",
    currentValue: "",
  });
  const [savingPosition, setSavingPosition] = useState(false);

  useEffect(() => {
    if (user) {
      void fetchSummary(user.id);
      void fetchBalances(user.id);
    }
  }, [fetchBalances, fetchSummary, user]);

  if (!user) {
    return <p>{t("portfolio.loginRequired")}</p>;
  }

  const availableTradingBalance = balances?.totalAvailable ?? 0;
  const investedAmountNumber = Number.parseFloat(form.investedAmount || "0");
  const hasFunds = availableTradingBalance > 0;
  const exceedsTradingBalance =
    Number.isFinite(investedAmountNumber) &&
    investedAmountNumber > availableTradingBalance;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!hasFunds) {
      toast({
        title: t("portfolio.insufficientBalanceTitle"),
        description: t("portfolio.insufficientBalanceDescription"),
        variant: "destructive",
      });
      return;
    }
    if (exceedsTradingBalance) {
      toast({
        title: t("portfolio.exceedsBalanceTitle"),
        description: t("portfolio.exceedsBalanceDescription", {
          amount: availableTradingBalance.toFixed(2),
        }),
        variant: "destructive",
      });
      return;
    }
    setSavingPosition(true);
    try {
      await upsertPosition({
        userId: user.id,
        assetSymbol: form.assetSymbol,
        assetName: form.assetName,
        investedAmount: Number(form.investedAmount),
        currentValue: Number(form.currentValue),
      });
      toast({
        title: t("portfolio.positionSavedTitle"),
        description: t("portfolio.positionSavedDescription", {
          symbol: form.assetSymbol.toUpperCase(),
        }),
        variant: "success",
      });
      setForm({
        assetSymbol: "",
        assetName: "",
        investedAmount: "",
        currentValue: "",
      });
      void fetchSummary(user.id);
    } catch (err) {
      toast({
        title: t("portfolio.errorTitle"),
        description: (err as string) ?? t("portfolio.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setSavingPosition(false);
    }
  };

  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-3xl font-semibold text-foreground">
          {t("portfolio.title")}
        </h2>
        <p className="text-sm text-muted-foreground dark:text-white/70">{t("portfolio.subtitle")}</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-3xl border border-border bg-muted/50 p-6 dark:border-white/10 dark:bg-black/30">
          <p className="text-sm text-muted-foreground dark:text-white/70">
            {t("portfolio.currentAllocations")}
          </p>
          {loading ? (
            <Skeleton className="mt-4 h-24 w-full" />
          ) : error ? (
            <p className="text-sm text-rose-600 dark:text-rose-300">{error}</p>
          ) : summary ? (
            <>
              <div className="mt-4 hidden lg:block">
                <AllocationDonut data={summary.allocations} />
              </div>
              <ul className="mt-4 divide-y divide-border dark:divide-white/10">
                {summary.allocations.map((allocation) => (
                  <li
                    key={allocation.assetSymbol}
                    className="flex items-center justify-between py-3 text-foreground/90 dark:text-white/80">
                    <div>
                      <p className="font-semibold">{allocation.assetName}</p>
                      <p className="text-xs text-muted-foreground dark:text-white/60">
                        {allocation.assetSymbol}
                      </p>
                    </div>
                    <div className="text-right">
                      <p>{allocation.currentValue.toFixed(2)} $</p>
                      <p className="text-xs text-muted-foreground dark:text-white/60">
                        {t("portfolio.allocationOfPortfolio", {
                          percentage: allocation.percentage,
                        })}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground dark:text-white/60">
              {t("portfolio.noPositions")}
            </p>
          )}
        </div>
        {summary && (
          <div className="rounded-3xl border border-border bg-muted/30 p-6 dark:border-white/10 dark:bg-white/5">
            <p className="text-sm text-muted-foreground dark:text-white/70">{t("portfolio.quickKpis")}</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <article className="rounded-2xl border border-border bg-card p-4 dark:border-white/10 dark:bg-black/20">
                <p className="text-xs text-muted-foreground dark:text-white/60">
                  {t("portfolio.totalValue")}
                </p>
                <p className="text-3xl font-semibold text-foreground">
                  {summary.totalValue.toFixed(2)} $
                </p>
              </article>
              <article className="rounded-2xl border border-border bg-card p-4 dark:border-white/10 dark:bg-black/20">
                <p className="text-xs text-muted-foreground dark:text-white/60">
                  {t("portfolio.invested")}
                </p>
                <p className="text-3xl font-semibold text-foreground">
                  {summary.totalInvested.toFixed(2)} $
                </p>
              </article>
              <article className="rounded-2xl border border-border bg-card p-4 sm:col-span-2 dark:border-white/10 dark:bg-black/20">
                <p className="text-xs text-muted-foreground dark:text-white/60">
                  {t("portfolio.performance")}
                </p>
                <p
                  className={`text-3xl font-semibold ${
                    summary.profitLoss >= 0
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-rose-600 dark:text-rose-400"
                  }`}>
                  {summary.profitLoss.toFixed(2)} $
                </p>
              </article>
            </div>
          </div>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className="grid gap-4 rounded-3xl border border-border bg-card p-6 md:grid-cols-2 dark:border-white/10 dark:bg-white/5">
        <div className="md:col-span-2">
          <h3 className="text-lg font-semibold text-foreground dark:text-white">
            {t("portfolio.formTitle")}
          </h3>
          <p className="text-sm text-muted-foreground dark:text-white/70">{t("portfolio.formSubtitle")}</p>
        </div>
        {!balancesLoading && !hasFunds && (
          <div className="md:col-span-2 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-800 dark:text-rose-200">
            {t("portfolio.insufficientBalanceDescription")}
          </div>
        )}
        {!balancesLoading && hasFunds && exceedsTradingBalance && (
          <div className="md:col-span-2 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-200">
            {t("portfolio.exceedsBalanceDescription", {
              amount: availableTradingBalance.toFixed(2),
            })}
          </div>
        )}
        <div className="space-y-2">
          <Label>{t("portfolio.symbolLabel")}</Label>
          <SelectRemote
            value={form.assetSymbol}
            onSelect={(project: { symbol: string; name: string }) => {
              setForm((prev) => ({
                ...prev,
                assetSymbol: project.symbol,
                assetName: project.name,
              }));
            }}
            placeholder={t("portfolio.symbolPlaceholder")}
            disabled={savingPosition}
          />
        </div>
        <div className="space-y-2">
          <Label>{t("portfolio.assetLabel")}</Label>
          <Input
            value={form.assetName}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                assetName: event.target.value,
              }))
            }
            placeholder={t("portfolio.assetPlaceholder")}
            required
            disabled={savingPosition}
          />
        </div>
        <div className="space-y-2">
          <Label>{t("portfolio.investedAmountLabel")}</Label>
          <Input
            type="number"
            value={form.investedAmount}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                investedAmount: event.target.value,
              }))
            }
            required
          />
        </div>
        <div className="space-y-2">
          <Label>{t("portfolio.currentValueLabel")}</Label>
          <Input
            type="number"
            value={form.currentValue}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                currentValue: event.target.value,
              }))
            }
            required
          />
        </div>
        <div className="md:col-span-2">
          <Button
            type="submit"
            disabled={
              savingPosition ||
              balancesLoading ||
              !hasFunds ||
              exceedsTradingBalance
            }>
            {savingPosition ? t("portfolio.saving") : t("portfolio.save")}
          </Button>
        </div>
      </form>
    </section>
  );
};

export default PortfolioPage;
